import type { TFunction } from 'i18next';

interface SentenceMakerOptionDefinition {
    value: string;
    labelKey: string;
    isCategory?: boolean;
}

interface SentenceMakerOption {
    value: string;
    label: string;
    isCategory?: boolean;
}

const difficultyOptionDefinitions: SentenceMakerOptionDefinition[] = [
    { value: 'category-primary', labelKey: 'experiment.generator.question.grade.category.primary', isCategory: true },
    { value: '小學一年級', labelKey: 'experiment.generator.question.grade.level.4' },
    { value: '小學二年級', labelKey: 'experiment.generator.question.grade.level.5' },
    { value: '小學三年級', labelKey: 'experiment.generator.question.grade.level.6' },
    { value: '小學四年級', labelKey: 'experiment.generator.question.grade.level.7' },
    { value: '小學五年級', labelKey: 'experiment.generator.question.grade.level.8' },
    { value: '小學六年級', labelKey: 'experiment.generator.question.grade.level.9' },
    { value: 'category-secondary', labelKey: 'experiment.generator.question.grade.category.secondary', isCategory: true },
    { value: '中學一級', labelKey: 'experiment.generator.question.grade.level.10' },
    { value: '中學二級', labelKey: 'experiment.generator.question.grade.level.11' },
    { value: '中學三級', labelKey: 'experiment.generator.question.grade.level.12' },
    { value: '中學四級', labelKey: 'experiment.generator.question.grade.level.13' },
    { value: '中學五級', labelKey: 'experiment.generator.question.grade.level.14' },
    { value: '中學六級', labelKey: 'experiment.generator.question.grade.level.15' },
];

const writingTechniqueOptionDefinitions: SentenceMakerOptionDefinition[] = [
    { value: '', labelKey: 'slide.components.sentenceMaker.writingTechnique.none' },
    { value: '抒情文', labelKey: 'slide.components.sentenceMaker.writingTechnique.category.lyricWriting', isCategory: true },
    { value: '直接抒情', labelKey: 'slide.components.sentenceMaker.writingTechnique.directExpression' },
    { value: '間接抒情', labelKey: 'slide.components.sentenceMaker.writingTechnique.indirectExpression' },
    { value: '描寫文', labelKey: 'slide.components.sentenceMaker.writingTechnique.category.descriptionWriting', isCategory: true },
    { value: '靜態描寫', labelKey: 'slide.components.sentenceMaker.writingTechnique.staticDescription' },
    { value: '動態描寫', labelKey: 'slide.components.sentenceMaker.writingTechnique.dynamicDescription' },
    { value: '多感官描寫', labelKey: 'slide.components.sentenceMaker.writingTechnique.multiSensoryDescription' },
    { value: '步移法', labelKey: 'slide.components.sentenceMaker.writingTechnique.stepMethod' },
    { value: '說明文', labelKey: 'slide.components.sentenceMaker.writingTechnique.category.explanatoryWriting', isCategory: true },
    { value: '定義說明', labelKey: 'slide.components.sentenceMaker.writingTechnique.definitionExplanation' },
    { value: '比喻說明', labelKey: 'slide.components.sentenceMaker.writingTechnique.metaphorExplanation' },
    { value: '數字說明', labelKey: 'slide.components.sentenceMaker.writingTechnique.numericExplanation' },
    { value: '分類說明', labelKey: 'slide.components.sentenceMaker.writingTechnique.categorizationExplanation' },
];

const rhetoricOptionDefinitions: SentenceMakerOptionDefinition[] = [
    { value: '', labelKey: 'slide.components.sentenceMaker.rhetoric.none' },
    { value: '比喻', labelKey: 'slide.components.sentenceMaker.rhetoric.metaphor' },
    { value: '擬人', labelKey: 'slide.components.sentenceMaker.rhetoric.personification' },
    { value: '借代', labelKey: 'slide.components.sentenceMaker.rhetoric.metonymy' },
    { value: '通感', labelKey: 'slide.components.sentenceMaker.rhetoric.synesthesia' },
    { value: '誇張', labelKey: 'slide.components.sentenceMaker.rhetoric.exaggeration' },
    { value: '反覆', labelKey: 'slide.components.sentenceMaker.rhetoric.repetition' },
    { value: '排比', labelKey: 'slide.components.sentenceMaker.rhetoric.parallelism' },
    { value: '對偶', labelKey: 'slide.components.sentenceMaker.rhetoric.antithesis' },
    { value: '對比', labelKey: 'slide.components.sentenceMaker.rhetoric.contrast' },
    { value: '反問', labelKey: 'slide.components.sentenceMaker.rhetoric.rhetoricalQuestion' },
    { value: '設問', labelKey: 'slide.components.sentenceMaker.rhetoric.questioning' },
    { value: '雙關', labelKey: 'slide.components.sentenceMaker.rhetoric.pun' },
    { value: '襯托', labelKey: 'slide.components.sentenceMaker.rhetoric.highlighting' },
    { value: '引用', labelKey: 'slide.components.sentenceMaker.rhetoric.quotation' },
];

const translateOptions = (optionDefinitions: SentenceMakerOptionDefinition[], t: TFunction): SentenceMakerOption[] => {
    return optionDefinitions.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
        isCategory: option.isCategory,
    }));
};

const findTranslatedLabelByValue = (optionDefinitions: SentenceMakerOptionDefinition[], value: string, t: TFunction): string | undefined => {
    const option = optionDefinitions.find((item) => !item.isCategory && item.value === value);
    return option ? t(option.labelKey) : undefined;
};

export const getDifficultyOptions = (t: TFunction): SentenceMakerOption[] => translateOptions(difficultyOptionDefinitions, t);

export const getWritingTechniqueOptions = (t: TFunction): SentenceMakerOption[] => translateOptions(writingTechniqueOptionDefinitions, t);

export const getRhetoricOptions = (t: TFunction): SentenceMakerOption[] => translateOptions(rhetoricOptionDefinitions, t);

export const getDifficultyLabel = (value: string, t: TFunction): string | undefined => findTranslatedLabelByValue(difficultyOptionDefinitions, value, t);

export const getWritingTechniqueLabel = (value: string, t: TFunction): string | undefined => findTranslatedLabelByValue(writingTechniqueOptionDefinitions, value, t);

export const getRhetoricLabel = (value: string, t: TFunction): string | undefined => findTranslatedLabelByValue(rhetoricOptionDefinitions, value, t);
